import React, { useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/shared/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Store, Phone, Building2, ChevronRight } from "lucide-react";

export default function MerchantRegister() {
    const { user, status, persistSession } = useAuth();
    const navigate = useNavigate();
    const [categories, setCategories] = React.useState([]);
    const [districts, setDistricts] = React.useState([]);

    const {
        register,
        handleSubmit,
        control,
        watch,
        formState: { errors, isSubmitting },
    } = useForm({
        defaultValues: {
            full_name: "",
            mobile_number: "",
            password: "",
            business_name: "",
            business_type: "OFFLINE",
            business_category: "",
            business_district: "",
        },
    });

    useEffect(() => {
        Promise.all([api.get("/categories"), api.get("/districts")])
            .then(([c, d]) => {
                setCategories(c.data);
                setDistricts(d.data);
            })
            .catch(() => toast.error("Could not load form data"));
    }, []);

    const values = watch();
    const canSubmit = useMemo(() => {
        return (
            values.full_name?.length >= 2 &&
            values.mobile_number?.length >= 10 &&
            values.password?.length >= 8 &&
            values.business_name?.length >= 1 &&
            !!values.business_type &&
            !!values.business_category &&
            !!values.business_district
        );
    }, [values]);

    if (status === "authenticated" && user) {
        // Already signed in — bounce directly without a blank frame.
        const to = user.role !== "SHOP_OWNER" ? "/dashboard" : "/merchant/home";
        return <Navigate to={to} replace />;
    }

    const onSubmit = async (data) => {
        try {
            const res = await api.post("/auth/shop/register", {
                ...data,
                mobile_number: normalizeMobile(data.mobile_number),
            });
            persistSession(res.data);
            toast.success("Account created! Now upload your documents.");
            navigate("/merchant/onboarding", { replace: true });
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };

    return (
        <div className="min-h-screen bg-neutral-50" data-testid="merchant-register-page">
            <div className="mx-auto flex min-h-screen max-w-[560px] flex-col bg-white">
                <header className="flex items-center gap-3 border-b border-neutral-200 bg-white px-4 py-4">
                    <Link to="/merchant/login">
                        <Logo size={88} variant="lockup" className="sm:hidden" />
                        <Logo size={120} variant="lockup" className="hidden sm:block" />
                    </Link>
                </header>
                <main className="flex-1 px-5 py-6">
                    <div className="mb-6 flex items-center gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-2xl nest-gradient text-white nest-shadow">
                            <Store size={22} />
                        </div>
                        <div>
                            <h1 className="text-xl font-extrabold text-neutral-900">List your shop</h1>
                            <p className="text-xs text-neutral-500">
                                Join Kerala's hyperlocal offer nest in 60 seconds.
                            </p>
                        </div>
                    </div>

                    <form className="space-y-4" onSubmit={handleSubmit(onSubmit)} data-testid="merchant-register-form">
                        <Field label="Your name">
                            <Input
                                placeholder="Full name"
                                autoComplete="name"
                                data-testid="mr-full-name"
                                {...register("full_name", { required: true })}
                            />
                        </Field>
                        <Field label="Mobile number">
                            <div className="relative">
                                <Phone
                                    size={16}
                                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
                                />
                                <Input
                                    className="pl-9"
                                    placeholder="98765 43210"
                                    inputMode="numeric"
                                    autoComplete="tel"
                                    data-testid="mr-mobile"
                                    {...register("mobile_number", {
                                        required: true,
                                        minLength: 10,
                                    })}
                                />
                            </div>
                            {errors.mobile_number && (
                                <p className="mt-1 text-xs text-rose-600">10-digit Indian mobile required</p>
                            )}
                        </Field>
                        <Field label="Set a password">
                            <Input
                                placeholder="At least 8 characters"
                                type="password"
                                autoComplete="new-password"
                                data-testid="mr-password"
                                {...register("password", { required: true, minLength: 8 })}
                            />
                            {errors.password && (
                                <p className="mt-1 text-xs text-rose-600">Min 8 characters</p>
                            )}
                        </Field>

                        <div className="border-t border-dashed border-neutral-200 pt-4">
                            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-neutral-800">
                                <Building2 size={16} className="text-orange-600" /> Business details
                            </div>

                            <Field label="Business name">
                                <Input
                                    placeholder="e.g. Kochi Fresh Supermart"
                                    data-testid="mr-business-name"
                                    {...register("business_name", { required: true })}
                                />
                            </Field>

                            <div className="mt-3">
                                <Label className="text-xs">Business type</Label>
                                <Controller
                                    control={control}
                                    name="business_type"
                                    render={({ field }) => (
                                        <RadioGroup
                                            value={field.value}
                                            onValueChange={field.onChange}
                                            className="mt-2 grid grid-cols-2 gap-3"
                                        >
                                            {["OFFLINE", "ONLINE"].map((bt) => (
                                                <label
                                                    key={bt}
                                                    className={`flex cursor-pointer items-center gap-2 rounded-xl border p-3 ${field.value === bt ? "border-orange-500 bg-orange-50" : "border-neutral-200"}`}
                                                    data-testid={`mr-bt-${bt}`}
                                                >
                                                    <RadioGroupItem value={bt} id={`bt-${bt}`} />
                                                    <span className="text-sm font-semibold">
                                                        {bt === "OFFLINE" ? "Physical" : "Online"}
                                                    </span>
                                                </label>
                                            ))}
                                        </RadioGroup>
                                    )}
                                />
                            </div>

                            <div className="mt-3 grid gap-3">
                                <div>
                                    <Label className="text-xs">Category</Label>
                                    <Controller
                                        control={control}
                                        name="business_category"
                                        rules={{ required: true }}
                                        render={({ field }) => (
                                            <Select
                                                value={field.value || undefined}
                                                onValueChange={field.onChange}
                                            >
                                                <SelectTrigger data-testid="mr-category">
                                                    <SelectValue placeholder="Choose category" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {categories.map((c) => (
                                                        <SelectItem key={c.slug} value={c.slug}>
                                                            {c.emoji} {c.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">District</Label>
                                    <Controller
                                        control={control}
                                        name="business_district"
                                        rules={{ required: true }}
                                        render={({ field }) => (
                                            <Select
                                                value={field.value || undefined}
                                                onValueChange={field.onChange}
                                            >
                                                <SelectTrigger data-testid="mr-district">
                                                    <SelectValue placeholder="Choose district" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {districts.map((d) => (
                                                        <SelectItem key={d.slug} value={d.slug}>
                                                            {d.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="sticky bottom-0 -mx-5 border-t border-neutral-200 bg-white px-5 pb-5 pt-3">
                            <Button
                                type="submit"
                                disabled={!canSubmit || isSubmitting}
                                className="h-12 w-full nest-gradient text-base font-semibold text-white"
                                data-testid="mr-submit"
                            >
                                {isSubmitting ? "Creating…" : "Create shop account"}
                                <ChevronRight size={16} className="ml-2" />
                            </Button>
                            <p className="mt-3 text-center text-xs text-neutral-500">
                                Already have an account?{" "}
                                <Link to="/merchant/login" className="font-semibold text-orange-600">
                                    Sign in
                                </Link>
                            </p>
                        </div>
                    </form>
                </main>
            </div>
        </div>
    );
}

const Field = ({ label, children }) => (
    <div>
        <Label className="text-xs">{label}</Label>
        <div className="mt-1">{children}</div>
    </div>
);

function normalizeMobile(v) {
    return (v || "").replace(/\s|-/g, "");
}
